import React, { useState } from 'react';
import { Share2, User } from 'lucide-react';
import { useMyQuizSelection } from '../../hooks/useMyQuizSelection';
import MyQuizIDShareComponent from './MyQuizIDShareComponent';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import QuizIcon from './QuizIcon';

/**
 * 自分が作成したクイズのリストを表示し、シェア機能を提供するコンポーネント
 * @param userId - 現在ログインしているユーザーのID
 */
interface MyQuizzesShareListProps {
    userId: string;
}

const MyQuizzesShareList: React.FC<MyQuizzesShareListProps> = ({ userId }) => {
    const { myQuizSelection, isLoading, isError } = useMyQuizSelection(userId);
    const isOnline = useOnlineStatus();

    // シェアモーダル用
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [quizToShare, setQuizToShare] = useState<{ id: string, title: string } | null>(null);

    // 自分のクイズのみをフィルタリング
    const myQuizzes = myQuizSelection?.quizSummaries?.filter(
        (quiz) => quiz.authorId === userId
    ) || [];

    if (isLoading) {
        return <div className="text-center text-gray-500">ロード中...</div>;
    }

    if (isError || !myQuizSelection) {
        return <div className="text-center text-red-500">クイズの読み込みに失敗しました。</div>;
    }

    return (
        <>
            <div className="p-4 border rounded-lg bg-gray-50">
                <h3 className="text-xl font-semibold text-gray-700 mb-4">自分が作ったクイズ</h3>
                {myQuizzes.length > 0 ? (
                    <ul className="space-y-3">
                        {myQuizzes.map((quizSummary) => (
                            <li key={quizSummary.id} className="p-4 border border-gray-200 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white space-y-4 sm:space-y-0">
                                <div className="flex items-start space-x-3 flex-1 min-w-0">
                                    {/* QuizIconコンポーネントを使用 */}
                                    <div className="flex-shrink-0 pt-1">
                                        <QuizIcon quizSummary={quizSummary} userId={userId} />
                                    </div>
                                    <div className="flex-1 text-left">
                                        <p className="font-semibold text-gray-900 break-words">{quizSummary.title}</p>
                                        {/* カテゴリを表示 */}
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {Array.isArray(quizSummary.category) ? (
                                                quizSummary.category.map((cat, index) => (
                                                    <span
                                                        key={index}
                                                        className="text-sm text-yellow-800 bg-yellow-100 px-3 py-1 rounded-full inline-block font-medium"
                                                    >
                                                        {cat}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="text-sm text-yellow-800 bg-yellow-100 px-3 py-1 rounded-full inline-block font-medium">
                                                    {quizSummary.category}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* 公開クイズの場合にのみシェアボタンを表示 */}
                                {quizSummary.isPublic && (
                                <button
                                    onClick={() => {
                                        setIsShareModalOpen(true);
                                        setQuizToShare({ id: quizSummary.id, title: quizSummary.title });
                                    }}
                                    disabled={!isOnline}
                                    className="ml-4 p-2 text-sky-500 hover:bg-sky-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                                    aria-label={`クイズ「${quizSummary.title}」をシェア`}
                                >
                                    <Share2 size={18} />
                                </button>
                                )}
                             </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-center text-gray-500 py-4">まだクイズを作成していません。</p>
                )}
            </div>

            {isShareModalOpen && quizToShare && (
                <MyQuizIDShareComponent
                    quizId={quizToShare.id}
                    quizTitle={quizToShare.title}
                    onClose={() => setIsShareModalOpen(false)}
                />
            )}
        </>
    );
};

export default MyQuizzesShareList;